import React from 'react';
import {
  Clock, RotateCcw, XCircle, CheckCircle2, AlertTriangle, Info,
} from 'lucide-react';
import { cn } from '../../../../lib/cn';

/**
 * CondicionesCancelacion · Fase 14b (#12).
 *
 * Componente presentacional que muestra las condiciones de
 * cancelación/reagendado de una cita al usuario.
 *
 * DOS MODOS de uso, según los datos disponibles:
 *
 * ── MODO A · Antes de confirmar reserva ───────────────────────────
 * La cita aún no existe. Se pintan las reglas de la CAMPAÑA y las
 * cancelaciones ya usadas por el usuario en esa campaña. Los datos
 * vienen de GET /mis-campanas/:id (ya ampliado en Fase 14a).
 *
 * Props para MODO A:
 *   fechaReserva: "YYYY-MM-DD"           // fecha que va a reservar
 *   horaReserva: "HH:MM"                 // hora que va a reservar
 *   horasAntelacionCancelar: number      // de la campaña
 *   cancelacionesMax: number             // max_cancelaciones o campana.max_cancelaciones_default
 *   cancelacionesUsadas: number          // del usuario en esa campaña
 *
 * ── MODO B · Ficha de cita existente (Mis Citas) ──────────────────
 * La cita existe. Se pintan las condiciones actuales derivadas del
 * endpoint GET /mis-citas/:citaId/condiciones.
 *
 * Props para MODO B:
 *   condiciones: {
 *     max_cancelaciones, cancelaciones_usadas, cancelaciones_restantes,
 *     horas_antelacion_min, fecha_hora_limite (ISO),
 *     puede_cancelar, puede_reagendar, motivo_no_puede,
 *   }
 *
 * Props opcionales comunes:
 *   variant: "compact" | "full" (default "full")
 *   className: extra para el contenedor
 */

function formatearFechaHora(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatearHoras(h) {
  if (h == null) return '';
  if (h === 0) return 'sin restricción';
  if (h === 1) return '1 hora';
  if (h < 24) return `${h} horas`;
  const dias = h / 24;
  if (dias === 1) return '1 día (24 h)';
  if (Number.isInteger(dias)) return `${dias} días (${h} h)`;
  return `${h} horas`;
}

// ────────────────────────────────────────────────────────────────

export default function CondicionesCancelacion(props) {
  const modo = props.condiciones ? 'B' : 'A';
  const variant = props.variant || 'full';

  if (modo === 'A') return <ModoAntesReserva {...props} variant={variant} />;
  return <ModoFichaCita {...props} variant={variant} />;
}

// ────────────────────────────────────────────────────────────────
//  MODO A · Antes de confirmar reserva
// ────────────────────────────────────────────────────────────────

function ModoAntesReserva({
  fechaReserva,
  horaReserva,
  horasAntelacionCancelar = 0,
  cancelacionesMax = 0,
  cancelacionesUsadas = 0,
  variant, className,
}) {
  const restantes = Math.max(0, cancelacionesMax - cancelacionesUsadas);

  // Calcular fecha límite: fechaReserva + horaReserva - horas antelación
  let fechaLimite = null;
  if (fechaReserva && horaReserva && horasAntelacionCancelar > 0) {
    const dhm = `${fechaReserva}T${horaReserva.length === 5 ? horaReserva + ':00' : horaReserva}`;
    const cita = new Date(dhm);
    if (!Number.isNaN(cita.getTime())) {
      fechaLimite = new Date(cita.getTime() - horasAntelacionCancelar * 3600 * 1000);
    }
  }

  return (
    <div className={cn(
      "border border-slate-200 rounded-lg bg-slate-50 p-3.5",
      className
    )}>
      <div className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-2.5">
        <Info className="w-3.5 h-3.5 text-slate-500" />
        Condiciones de cancelación
      </div>

      <ul className="space-y-1.5 text-xs text-slate-700">
        {cancelacionesMax > 0 ? (
          <>
            <ItemCondicion icon={RotateCcw}>
              Puedes cancelar o reagendar esta cita hasta{' '}
              <strong>{cancelacionesMax} {cancelacionesMax === 1 ? 'vez' : 'veces'}</strong>.
            </ItemCondicion>
            {cancelacionesUsadas > 0 && (
              <ItemCondicion icon={CheckCircle2}>
                Ya has usado <strong>{cancelacionesUsadas} de {cancelacionesMax}</strong> cambios.
                Te {restantes === 1 ? 'queda' : 'quedan'}{' '}
                <strong>{restantes}</strong>.
              </ItemCondicion>
            )}
          </>
        ) : (
          <ItemCondicion icon={XCircle}>
            Esta campaña <strong>no permite cancelaciones</strong>.
          </ItemCondicion>
        )}

        <ItemCondicion icon={Clock}>
          {horasAntelacionCancelar > 0 ? (
            <>
              Solo podrás cancelar o reagendar con un mínimo de{' '}
              <strong>{formatearHoras(horasAntelacionCancelar)}</strong>{' '}
              de antelación.
            </>
          ) : (
            <>Podrás cancelar o reagendar hasta la hora de la cita.</>
          )}
        </ItemCondicion>

        {fechaLimite && (
          <ItemCondicion icon={AlertTriangle}>
            Fecha límite para modificar:{' '}
            <strong>{formatearFechaHora(fechaLimite.toISOString())}</strong>.
          </ItemCondicion>
        )}
      </ul>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  MODO B · Ficha de cita existente
// ────────────────────────────────────────────────────────────────

function ModoFichaCita({ condiciones, variant, className }) {
  if (!condiciones) return null;

  const {
    max_cancelaciones,
    cancelaciones_usadas,
    cancelaciones_restantes,
    horas_antelacion_min,
    fecha_hora_limite,
    puede_cancelar,
    puede_reagendar,
    motivo_no_puede,
  } = condiciones;

  const puedeCancelarOReagendar = puede_cancelar || puede_reagendar;
  const cambios = max_cancelaciones ?? 0;

  return (
    <div className={cn(
      "border rounded-lg p-3.5",
      puedeCancelarOReagendar
        ? "border-slate-200 bg-slate-50"
        : "border-amber-200 bg-amber-50",
      className
    )}>
      <div className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-2.5">
        <Info className="w-3.5 h-3.5 text-slate-500" />
        Condiciones de esta cita
      </div>

      <ul className="space-y-1.5 text-xs text-slate-700">
        {cambios > 0 ? (
          <ItemCondicion icon={RotateCcw}>
            Has usado <strong>{cancelaciones_usadas} de {cambios}</strong> cambios.
            {cancelaciones_restantes > 0 && (
              <> Te {cancelaciones_restantes === 1 ? 'queda' : 'quedan'}{' '}
                <strong>{cancelaciones_restantes}</strong>.</>
            )}
          </ItemCondicion>
        ) : (
          <ItemCondicion icon={XCircle}>
            Esta campaña <strong>no permite cancelaciones</strong>.
          </ItemCondicion>
        )}

        <ItemCondicion icon={Clock}>
          {horas_antelacion_min > 0 ? (
            <>Antelación mínima: <strong>{formatearHoras(horas_antelacion_min)}</strong>.</>
          ) : (
            <>Sin antelación mínima (hasta la hora de la cita).</>
          )}
        </ItemCondicion>

        {fecha_hora_limite && puedeCancelarOReagendar && (
          <ItemCondicion icon={AlertTriangle}>
            Podrás modificar hasta el{' '}
            <strong>{formatearFechaHora(fecha_hora_limite)}</strong>.
          </ItemCondicion>
        )}

        {!puedeCancelarOReagendar && motivo_no_puede && (
          <li className="pt-2 mt-2 border-t border-amber-200 flex items-start gap-1.5 text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{motivo_no_puede}</span>
          </li>
        )}
      </ul>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────

function ItemCondicion({ icon: Icon, children }) {
  return (
    <li className="flex items-start gap-1.5">
      <Icon className="w-3 h-3 shrink-0 mt-0.5 text-slate-400" />
      <span className="flex-1">{children}</span>
    </li>
  );
}
